import { ColumnType, Input, Pagination, Table } from "@/components";
import DateRangePicker from "@/components/DateRangePicker";
import { useGetMyNotificationByPages } from "@/hooks/notifications/useGetMyNotificationByPages";
import { useDebounce } from "@/hooks/useDebounce";
import {
  GetNotificationsParams,
  MyNotificationDto,
  MyNotiStatus,
} from "@/types";
import { formatDate } from "@/utils";
import { endOfDay } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoIosSearch } from "react-icons/io";
import { useNotificationTable } from "../hooks";
import DetailMyNotificationDrawer from "./DetailMyNotificationDrawer";

interface Props {
  isMember?: boolean;
}

const MyNotificationTable = ({ isMember = false }: Props) => {
  const isInitialMount = useRef(true);

  // states to filter data
  const [filters, setFilters] = useState<Omit<GetNotificationsParams, "page">>({
    limit: 10,
  });

  // Open drawer for detail notification
  const [selectedNotification, setSelectedNotification] =
    useState<MyNotificationDto | null>(null);

  const [inputValue, setInputValue] = useState<string>("");
  const debouncedSearchTerm = useDebounce(inputValue.trim(), 500);

  // Reset page when filter values change
  useEffect(() => {
    setPage(1);
  }, [filters, debouncedSearchTerm]);

  const { handleCopy } = useNotificationTable();

  useEffect(() => {
    // Skip updating search params on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
  }, [debouncedSearchTerm]);

  // handle date range change
  const handleDateChange = (date: { start: string; end: string }) => {
    setFilters({
      ...filters,
      startDate: date.start,
      endDate: date.end ? endOfDay(date.end).toISOString() : undefined,
    });
  };

  // define columns for table
  const columns: ColumnType<MyNotificationDto>[] = useMemo(() => {
    return [
      {
        title: "通知テーマ",
        key: "title",
        isTruncated: true,
        displayTitle: true,
        width: 140,
        render: (_, record: MyNotificationDto) => record.notification?.title,
      },
      {
        title: "通知内容",
        key: "description",
        isTruncated: true,
        displayTitle: true,
        width: 200,
        render: (_, record: MyNotificationDto) =>
          record.notification?.description,
      },
      {
        title: "通知日",
        key: "date",
        isTruncated: true,
        displayTitle: true,
        width: 140,
        render: (_, record: MyNotificationDto) =>
          record.created_at ? formatDate(record.created_at) : "",
      },
      {
        title: "通知者",
        key: "manager",
        width: 140,
        isTruncated: true,
        displayTitle: true,
        render: (_, record: MyNotificationDto) => record.notification?.manager,
      },
    ];
  }, []);

  // handle click row to show detail
  const handleOnRow = (record: MyNotificationDto) => {
    setSelectedNotification(record);
  };

  // query data from api
  const { myNotifications, pagination, isLoading, setPage } =
    useGetMyNotificationByPages({ ...filters, s: debouncedSearchTerm });

  // check notification row with status UNREAD  to highlight
  const checkHighlightRow = useCallback((record: MyNotificationDto) => {
    return record.status !== MyNotiStatus.READ;
  }, []);

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 py-4 justify-start">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="通知テーマで検索"
            icon={<IoIosSearch className="size-4" />}
            className="!h-10 w-80"
          />

          <DateRangePicker
            containerClassName="md:ml-auto"
            value={{
              start: filters.startDate || "",
              end: filters.endDate || "",
            }}
            onChange={handleDateChange}
          />
        </div>
        <Table<MyNotificationDto>
          columns={columns as any}
          dataSource={myNotifications}
          loading={isLoading}
          bordered
          onRow={(record) => {
            return {
              onClick: () => {
                handleOnRow(record);
              },
            };
          }}
          shouldHighlightRow={checkHighlightRow}
          showAction={!isMember}
          renderAction={(record) => [
            {
              title: "複製",
              // Only copy noti content, not the recivers
              action: () => handleCopy(record.notification!, true),
            },
          ]}
        />
        {pagination && (
          <Pagination
            totalPages={pagination.pageCount}
            currentPage={pagination.page}
            onPageChange={setPage}
          />
        )}
      </div>

      {!!selectedNotification?.notification_id && (
        <DetailMyNotificationDrawer
          open={!!selectedNotification}
          onClose={() => setSelectedNotification(null)}
          notificationId={selectedNotification.notification_id}
        />
      )}
    </>
  );
};

export default MyNotificationTable;
